import { useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from "recharts";
import type { FunnelStep } from "./sql";
import { METRIC_OPTIONS } from "./sql";
import { isoWeekKey, monthLabel, pctVal, daysBetween } from "../../lib/funnel/helpers";
import type { WeekKey, PathFilter, SignupCohortEntry } from "./CohortGrid";
import { WEEK_BUCKETS } from "./CohortGrid";
import { PRIMARY, STEEL, FG1, MUTED } from "../../lib/funnel/theme";
import { SectionCardHeader } from "../../components/ChartCard";
import type { FilterPillSpec } from "../../components/FilterPills";

type Granularity = "month" | "week";

function bucketDate(d: string, g: Granularity): string {
  if (!d) return "";
  return g === "month" ? d.slice(0, 7) : isoWeekKey(d);
}

// First day of a bucket key. For "YYYY-MM" → 1st of month; for "YYYY-Www" →
// ISO Monday of that week.
function bucketStartMs(key: string, g: Granularity): number {
  if (g === "month") return new Date(key + "-01T00:00:00Z").getTime();
  const m = key.match(/^(\d{4})-W(\d{2})$/);
  if (!m) return NaN;
  const year = Number(m[1]);
  const week = Number(m[2]);
  const jan4 = Date.UTC(year, 0, 4);
  const day = new Date(jan4).getUTCDay() || 7;
  const monday = jan4 - (day - 1) * 86400000;
  return monday + (week - 1) * 7 * 86400000;
}

export function ActivationOverTime({
  signupCohort,
  metric,
  selectedWeek,
  onMetricChange,
  pathFilter,
  onPathFilterChange,
  pagePills,
}: {
  signupCohort: SignupCohortEntry[];
  metric: FunnelStep;
  selectedWeek: WeekKey;
  onMetricChange: (key: string) => void;
  pathFilter: PathFilter;
  onPathFilterChange: (p: PathFilter) => void;
  pagePills?: FilterPillSpec[];
}) {
  const [granularity, setGranularity] = useState<Granularity>("month");

  const cap = useMemo(
    () => WEEK_BUCKETS.find((w) => w.key === selectedWeek)?.cap ?? null,
    [selectedWeek],
  );

  const data = useMemo(() => {
    const buckets = new Map<string, { N: number; hits: number; label: string }>();

    const filtered = signupCohort.filter((e) =>
      pathFilter === "demo" ? e.isDemoPath
      : pathFilter === "self" ? !e.isDemoPath
      : true,
    );
    for (const e of filtered) {
      const k = bucketDate(e.cohortDate, granularity);
      if (!k) continue;
      const b = buckets.get(k) ?? { N: 0, hits: 0, label: granularity === "month" ? monthLabel(k) : k };
      b.N += 1;
      if (metric.predicate(e.user)) {
        if (cap == null) b.hits += 1;
        else {
          const first = metric.firstDateOf(e.user);
          if (first && daysBetween(e.cohortDate, first) <= cap * 7) b.hits += 1;
        }
      }
      buckets.set(k, b);
    }

    // Drop buckets whose week-cap window hasn't fully elapsed yet — matches
    // the cohort grid's blank-future-week rule, otherwise the right tail of
    // the line shows incomplete-window data points.
    const todayMs = Date.now();
    const keys = Array.from(buckets.keys())
      .sort()
      .filter((k) => {
        if (cap == null) return true;
        const start = bucketStartMs(k, granularity);
        return Number.isFinite(start) && start + cap * 7 * 86400000 <= todayMs;
      });
    return keys.map((k) => {
      const b = buckets.get(k);
      if (!b) return { bucket: k, label: k, n: 0, pct: 0, hits: 0 };
      return {
        bucket: k,
        label: b.label,
        n: b.N,
        pct: Number(pctVal(b.hits, b.N).toFixed(1)),
        hits: b.hits,
      };
    });
  }, [signupCohort, granularity, metric, cap, pathFilter]);

  const seriesLabel = cap == null
    ? `${metric.label} (lifetime)`
    : `${metric.label} within ${cap} week${cap === 1 ? "" : "s"} of cohort start`;

  const lineColor = pathFilter === "self" ? STEEL : PRIMARY;

  const pills: FilterPillSpec[] = [...(pagePills ?? [])];
  pills.push({ kind: "mode", label: `Metric: ${metric.label}` });
  pills.push({ kind: "mode", label: `Bucket: ${granularity === "month" ? "monthly" : "weekly"}` });
  if (pathFilter !== "all") {
    pills.push({ kind: "mode", label: pathFilter === "demo" ? "Path: demo only" : "Path: self-serve only" });
  }

  return (
    <section className="card">
      <SectionCardHeader
        title="Activation rates over time"
        subtitle={`${seriesLabel}, by cohort. Hover for raw counts.`}
        filterPills={pills}
        headerExtra={
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <label style={{ fontSize: 11, color: FG1 }}>Metric</label>
            <select
              value={metric.key}
              onChange={(e) => onMetricChange(e.target.value)}
              style={{
                padding: "3px 8px",
                fontSize: 11,
                border: `1px solid ${MUTED}`,
                borderRadius: 6,
                background: "#fff",
                color: FG1,
              }}
            >
              {METRIC_OPTIONS.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
            <div role="group" aria-label="Path filter" style={{ display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11 }}>
              {([
                { key: "all", label: "All" },
                { key: "demo", label: "Demo" },
                { key: "self", label: "Self" },
              ] as const).map((opt) => {
                const active = pathFilter === opt.key;
                const bg = active ? (opt.key === "self" ? STEEL : PRIMARY) : "transparent";
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => onPathFilterChange(opt.key)}
                    style={{
                      padding: "3px 12px",
                      background: bg,
                      color: active ? "#fff" : FG1,
                      border: "none",
                      cursor: "pointer",
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div role="group" aria-label="Bucket granularity" style={{ display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11 }}>
              {(["month", "week"] as const).map((g) => {
                const active = granularity === g;
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGranularity(g)}
                    style={{
                      padding: "3px 10px",
                      background: active ? PRIMARY : "transparent",
                      color: active ? "#fff" : FG1,
                      border: "none",
                      cursor: "pointer",
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {g === "month" ? "Month" : "Week"}
                  </button>
                );
              })}
            </div>
          </div>
        }
      />
      <div className="card-body">
        {data.length === 0 ? (
          <p className="muted">No data for the current filter.</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="4 4" stroke="rgba(0,0,0,0.15)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: MUTED }} />
              <YAxis
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 10, fill: MUTED }}
                width={42}
              />
              <Tooltip
                formatter={(value, _name, item) => {
                  const row = item?.payload as { hits?: number; n?: number } | undefined;
                  const hits = row?.hits ?? 0;
                  const total = row?.n ?? 0;
                  return [`${value}% (${hits}/${total})`, seriesLabel] as [string, string];
                }}
              />
              <Line
                type="monotone"
                dataKey="pct"
                name={metric.key}
                stroke={lineColor}
                strokeWidth={2}
                dot={{ r: 2 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
